import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react';
import { useColorModeValue } from '@chakra-ui/system';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { MdBarChart } from 'react-icons/md';
import Card from '../../../components/card/Card';
import { translate } from '../../../i18n';
import { useChartOptions } from '../../../hooks/useChartOptions';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { getDemoUserLevelsDistribution, isDemoOrganization } from '../demoAnalyticsData';
import { useGetUserLevelsDistribution } from '../hooks/useGetUserLevelsDistribution';
import ChartReadState, { chartHasSomethingToDraw } from './ChartReadState';

const UserLevelsDistributionChart = () => {
    const { selectedOrganization } = useSelectedOrganization();
    const orgId = selectedOrganization?.id;
    const isDemo = isDemoOrganization(orgId);
    const { data: realData, isInitialLoading, isError, refetch } = useGetUserLevelsDistribution(orgId);
    const chartData = useMemo(() => isDemo ? getDemoUserLevelsDistribution() : realData, [isDemo, realData]);
    /**
     * This component took NO error flag before this run, and the bar chart is
     * drawn from `(chartData || []).map(...)` — so a REFUSED read and a read
     * that was never ISSUED both rendered an empty axis, which on this page
     * reads as "this organization has no players at any level".
     *
     * The gate here is `enabled: !!organizationId`, which is the band run 100
     * measured as reachable on EVERY admin page: `useGetMyOrganizations`
     * carries `placeholderData: []`, so a refused or empty org list leaves the
     * auto-select unfired and `selectedOrganization` undefined for the whole
     * session while the admin layout renders anyway. The empty chart was
     * therefore permanent, not transient.
     */
    const readState = isDemo ? 'answered' : classifyRead(isError, isInitialLoading, realData);

    const seriesData: ApexAxisChartSeries = useMemo(() => {
        return [{
            name: translate('tabs.users'),
            data: (chartData || []).map(d => d.count),
        }];
    }, [chartData]);

    const categories = useMemo(() => {
        return (chartData || []).map(d => d.level);
    }, [chartData]);

    const baseChart = useChartOptions();
    const options: ApexCharts.ApexOptions = {
        ...baseChart,
        chart: {
            ...baseChart.chart,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                columnWidth: '60%',
            },
        },
        xaxis: {
            categories,
            title: { text: translate('analytics.level') },
            labels: {
                style: { fontSize: '12px' },
            },
        },
        yaxis: {
            labels: {
                formatter: (val) => Math.round(val).toString(),
            },
        },
        tooltip: {
            ...baseChart.tooltip,
            y: {
                formatter: (val) => `${Math.round(val)} ${translate('tabs.users').toLowerCase()}`,
            },
        },
    };

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const iconColor = useColorModeValue('brand.500', 'white');
    const bgButton = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
    const bgHover = useColorModeValue({ bg: 'secondaryGray.400' }, { bg: 'whiteAlpha.50' });
    const bgFocus = useColorModeValue({ bg: 'secondaryGray.300' }, { bg: 'whiteAlpha.100' });

    return (
        <Card alignItems='center' flexDirection='column' w='100%'>
            <Flex align='center' w='100%' px='15px' py='10px'>
                <Text me='auto' color={textColor} fontSize='xl' fontWeight='700' lineHeight='100%'>
                    {translate('analytics.user_levels_distribution')}
                </Text>
                {Boolean(!chartData && readState === 'loading') && <Button
                    isLoading
                    alignItems='center'
                    justifyContent='center'
                    bg={bgButton}
                    _hover={bgHover}
                    _focus={bgFocus}
                    _active={bgFocus}
                    w='37px'
                    h='37px'
                    lineHeight='100%'
                    borderRadius='10px'
>
                    <Icon as={MdBarChart} color={iconColor} w='24px' h='24px' />
                </Button>}
            </Flex>

            <Box h='350px' mt='auto' w='100%'>
                {!chartHasSomethingToDraw(readState) ? (
                    <ChartReadState state={readState} gate='organization' onRetry={() => { refetch(); }} />
                ) : (
                    <ReactApexChart
                        key={`user-levels-${isDemo}`}
                        options={options}
                        series={seriesData}
                        type="bar"
                        height={350}
                    />
                )}
            </Box>
        </Card>
    );
};

export default UserLevelsDistributionChart;
